// ═══════════════════════════════════════════════════════════════
// parseNavigation — convert QuickTaskBar input into a nav action
// Path: src/utils/parseNavigation.js
//
// Recognizes:
//   - Slash commands: /h, /home, /donors, /find Sarah, /today, etc.
//   - Natural language: "go home", "open gifts", "find Sarah"
//
// Returns null if the input is not a recognized navigation directive.
// The caller (QuickTaskBar) then falls through to existing task
// parsing, so the same Ctrl+K bar serves both purposes.
//
// Returned shape:
//   { type: "nav",        view, tab?, label }
//   { type: "search",     search, label }
//   { type: "drawer",     label }
//   { type: "help",       label }
//   { type: "pollenMenu", label }
//   { type: "pollenCard", cardKey, label }
// ═══════════════════════════════════════════════════════════════

import { POLLEN_CARD_REGISTRY } from "../components/dashboard/pollen/PollenGrid";

// Fuzzy match a free-text arg against a pollen card. Matches on key,
// on any word in the label, or on the full lowercased label.
const matchPollenCard = (arg) => {
  if (!arg) return null;
  const needle = arg.toLowerCase().trim();
  if (!needle) return null;

  // 1. Exact key match (e.g. "retention_danger", "sybnt")
  const keyHit = POLLEN_CARD_REGISTRY.find((c) => c.key.toLowerCase() === needle);
  if (keyHit) return keyHit;

  // 2. Label starts-with or contains a word matching the needle
  const labelHit = POLLEN_CARD_REGISTRY.find((c) => {
    const label = c.label.toLowerCase();
    if (label === needle) return true;
    if (label.startsWith(needle)) return true;
    return label.split(/\s+/).some((word) => word.startsWith(needle));
  });
  if (labelHit) return labelHit;

  // 3. Label contains the needle anywhere
  return (
    POLLEN_CARD_REGISTRY.find((c) => c.label.toLowerCase().includes(needle)) ||
    null
  );
};

// ── Single-word slash commands ───────────────────────────────
const SLASH_COMMANDS = {
  // Dashboard
  h:         { type: "nav", view: "main", label: "Go to Dashboard" },
  home:      { type: "nav", view: "main", label: "Go to Dashboard" },
  dashboard: { type: "nav", view: "main", label: "Go to Dashboard" },

  // Donors list
  d:      { type: "nav", view: "donors", label: "Open Donors list" },
  donors: { type: "nav", view: "donors", label: "Open Donors list" },

  // Gifts
  g:     { type: "nav", view: "gifts", label: "Open Gifts" },
  gifts: { type: "nav", view: "gifts", label: "Open Gifts" },

  // Cultivation pipeline
  c:           { type: "nav", view: "cultivation", label: "Open Cultivation pipeline" },
  cultivation: { type: "nav", view: "cultivation", label: "Open Cultivation pipeline" },
  pipeline:    { type: "nav", view: "cultivation", label: "Open Cultivation pipeline" },

  // Capacity portfolio view
  capacity:    { type: "nav", view: "capacity", label: "Open Capacity portfolio" },
  cap:         { type: "nav", view: "capacity", label: "Open Capacity portfolio" },

  // Tasks view
  t:     { type: "nav", view: "tasks", label: "Open Tasks view" },
  tasks: { type: "nav", view: "tasks", label: "Open Tasks view" },

  // Reports
  r:       { type: "nav", view: "reports", label: "Open Reports" },
  reports: { type: "nav", view: "reports", label: "Open Reports" },

  // Events
  e:      { type: "nav", view: "events", label: "Open Events" },
  events: { type: "nav", view: "events", label: "Open Events" },

  // Board portfolios
  p:          { type: "nav", view: "portfolios", label: "Open Board portfolios" },
  portfolios: { type: "nav", view: "portfolios", label: "Open Board portfolios" },

  // Settings
  s:        { type: "nav", view: "settings", label: "Open Settings" },
  settings: { type: "nav", view: "settings", label: "Open Settings" },

  // Reports → Contact Reports tab
  cr: {
    type: "nav",
    view: "reports",
    tab: "contactReports",
    label: "Open Reports → Contact Reports",
  },
  contactreports: {
    type: "nav",
    view: "reports",
    tab: "contactReports",
    label: "Open Reports → Contact Reports",
  },
  "contact-reports": {
    type: "nav",
    view: "reports",
    tab: "contactReports",
    label: "Open Reports → Contact Reports",
  },

  // Task drawer overlay
  today: { type: "drawer", label: "Open today's task drawer" },

  // Pollen card menu (no arg → submenu of all cards)
  pollen: { type: "pollenMenu", label: "Choose a Pollen card" },

  // Log a contact on the currently-open donor
  log:         { type: "logContact", label: "Log contact on this donor" },
  logcontact:  { type: "logContact", label: "Log contact on this donor" },
  "log-contact": { type: "logContact", label: "Log contact on this donor" },

  // Help
  "?":  { type: "help", label: "Show command list" },
  help: { type: "help", label: "Show command list" },
};

// ── Slash commands that take an argument ─────────────────────
const SLASH_COMMANDS_WITH_ARG = {
  find:   (arg) => ({ type: "search", search: arg, label: `Find donors: "${arg}"` }),
  search: (arg) => ({ type: "search", search: arg, label: `Find donors: "${arg}"` }),
  pollen: (arg) => {
    const card = matchPollenCard(arg);
    if (!card) return null;
    return {
      type: "pollenCard",
      cardKey: card.key,
      label: `Open Pollen card: ${card.label}`,
    };
  },
};

// ── Main parser ──────────────────────────────────────────────
export const parseNavigation = (input) => {
  if (!input || !input.trim()) return null;
  const lower = input.trim().toLowerCase();

  // ── Slash commands ──
  if (lower.startsWith("/")) {
    const body = lower.slice(1).trim();
    if (!body) return null;

    // Try arg-taking commands first (e.g. "/find sarah")
    const firstSpaceIdx = body.indexOf(" ");
    if (firstSpaceIdx !== -1) {
      const verb = body.slice(0, firstSpaceIdx);
      const arg = body.slice(firstSpaceIdx + 1).trim();
      const argFn = SLASH_COMMANDS_WITH_ARG[verb];
      if (argFn && arg) return argFn(arg);
    }

    // Single-word commands
    const cmd = SLASH_COMMANDS[body];
    if (cmd) return cmd;

    // /find or /search by itself → just open the donors list
    if (body === "find" || body === "search") {
      return { type: "nav", view: "donors", label: "Open Donors list" };
    }

    return null;
  }

  // ── Natural language ──

  // "go home" / "home"
  if (/^(go\s+)?home$/.test(lower)) {
    return { type: "nav", view: "main", label: "Go to Dashboard" };
  }

  // "open gifts" / "show gifts" / "view gifts" / "enter gifts"
  if (/^(open|show|view|enter)\s+gifts$/.test(lower)) {
    return { type: "nav", view: "gifts", label: "Open Gifts" };
  }

  // "find <name>" / "search <name>" / "donor search <name>"
  const findMatch = lower.match(/^(?:find|search|donor\s+search)\s+(.+)$/);
  if (findMatch) {
    const term = findMatch[1].trim();
    return {
      type: "search",
      search: term,
      label: `Find donors: "${term}"`,
    };
  }

  return null;
};

// ── Help panel content (rendered when type === "help") ──────
export const NAV_COMMAND_LIST = [
  { keys: "/h  /home  go home",         action: "Dashboard" },
  { keys: "/d  /donors",                 action: "Donors list" },
  { keys: "/g  /gifts  open gifts",      action: "Gifts" },
  { keys: "/c  /cultivation  /pipeline", action: "Cultivation pipeline" },
  { keys: "/capacity  /cap",                action: "Capacity portfolio view" },
  { keys: "/t  /tasks",                  action: "Tasks view" },
  { keys: "/r  /reports",                action: "Reports" },
  { keys: "/cr  /contactreports",        action: "Reports → Contact Reports tab" },
  { keys: "/e  /events",                 action: "Events" },
  { keys: "/p  /portfolios",             action: "Board portfolios" },
  { keys: "/s  /settings",               action: "Settings" },
  { keys: "/find <name>  find <name>",   action: "Donors list filtered by name" },
  { keys: "/today",                       action: "Open task drawer" },
  { keys: "/pollen  /pollen <name>",       action: "Pollen card menu / jump to a card" },
  { keys: "/log  /logcontact",              action: "Log contact on the current donor" },
  { keys: "/help  /?",                    action: "Show this list" },
];
